import React from "react";
import { Box, Typography } from "@mui/material";

const DemoPromoComponent = () => {
  const steps = [
    {
      emoji: "🎬",
      title: "Upload Product Video",
      description: "Share a video walkthrough of your application"
    },
    {
      emoji: "🧪",
      title: "Review Tests",
      description: "Approve AI-generated test scenarios"
    },
    {
      emoji: "📧",
      title: "Receive Suite",
      description: "Get Playwright tests delivered in your email"
    }
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        pl: 20,
        pr: 6,
        py: 4,
        backgroundColor: "transparent",
        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
        boxSizing: "border-box",
      }}
    >
      {/* Main Title */}
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            mb: 2,
            letterSpacing: "-0.02em",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            fontSize: { xs: "2rem", md: "2.5rem" },
            lineHeight: 1.2,
          }}
        >
          Get a Playwright Test Suite{" "}
          <span
            style={{
              textDecoration: "underline wavy",
              textDecorationColor: "#a855f7",
              textUnderlineOffset: "4px",
              textDecorationThickness: "2px",
            }}
          >
            Overnight
          </span>
        </Typography>
        
        <Typography
          variant="h6"
          sx={{
            color: "#94a3b8",
            fontWeight: 400,
            lineHeight: 1.5,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            fontSize: "1.1rem",
          }}
        >
          Convert your product demo into comprehensive Playwright test suites
        </Typography>
      </Box>

      {/* Steps */}
      <Box sx={{ mb: 5 }}>
        {steps.map((step, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              mb: 3,
              opacity: 0,
              animation: `fadeInUp 0.5s ease forwards ${index * 0.15 + 0.2}s`,
              "@keyframes fadeInUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(15px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <Box
              sx={{
                mr: 3,
                mt: 0.5,
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                fontSize: "18px",
              }}
            >
              {step.emoji}
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 600,
                  mb: 0.5,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  fontSize: "1.1rem",
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{
                  color: "#94a3b8",
                  lineHeight: 1.5,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  maxWidth: "420px",
                  fontSize: "1.05rem",
                }}
              >
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Requirements */}
      <Box
        sx={{
          opacity: 0,
          animation: "fadeInUp 0.5s ease forwards 0.8s",
          "@keyframes fadeInUp": {
            "0%": {
              opacity: 0,
              transform: "translateY(15px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            borderRadius: 2,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            p: 3,
            mb: 2,
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#ffffff",
              fontWeight: 600,
              mb: 2,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              fontSize: "1.15rem",
            }}
          >
            Requirements
          </Typography>
          
          <Box
            component="ul"
            sx={{
              color: "#94a3b8",
              pl: 2,
              m: 0,
              "& > li": {
                mb: 1.5,
                fontSize: "1.1rem",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                lineHeight: 1.5,
              },
            }}
          >
            <li>Publicly accessible application URL</li>
            <li>No CAPTHCA or bot detection</li>
            <li>Test credentials for authenticated flows</li>
          </Box>
        </Box>
        
        <Typography
          variant="caption"
          sx={{
            color: "#64748b",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            fontSize: "0.95rem",
          }}
        >
          Your tests will not be generated if you do not meet the requirements.
        </Typography>
      </Box>
    </Box>
  );
};

export default DemoPromoComponent; 